/**
 * Single Source of Truth — App navigation hierarchy.
 *
 * IMPORTANT: every URL has exactly ONE owning primary section. This eliminates
 * the "navigation jumps when you click a sub-link" problem that plagued the
 * earlier design where the same route was listed under multiple primaries
 * (e.g. `/elevations` was claimed by both "Policies" and "Access").
 *
 * To add a new page, put its route in the appropriate section's `links` array.
 * Both `PrimarySidebar` and `SecondaryNav` derive their behaviour from this file.
 */

import {
  AlertTriangle,
  Bot,
  Cloud,
  FileKey,
  LayoutDashboard,
  Lock,
  Radar,
  Settings,
  Settings2,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { TenantFeatureKey } from "../lib/tenant-feature-flags.js";
import type { ExperimentalBetKey } from "@identity-armour/shared/experimental-bets/completeness-gate";

export interface NavLinkDef {
  /** Stable id (used as React key + for tests). */
  id: string;
  /** Display label in the secondary nav. */
  label: string;
  /** Route path (matches React Router `Route path`). */
  to: string;
  /** If true, only highlight when pathname === to exactly. */
  end?: boolean;
  /** Visual hint that this link is a sub-item of the previous link. */
  indent?: boolean;
  /**
   * Optional sub-group label. When a link's `groupLabel` differs from the
   * previous visible link's, the secondary nav renders a small uppercase header
   * above it. Purely presentational; does not affect routing or ownership.
   */
  groupLabel?: string;
  /**
   * Optional tenant feature flag gating this link. When set, the link is only
   * shown if `tenant.settings.features[featureKey]` is enabled.
   */
  featureKey?: TenantFeatureKey;
  /**
   * Optional experimental-bet gate (Sprint S6 completeness gate). When set, the
   * link is only shown if the bet is enabled for the tenant AND its registry
   * completeness score has reached the lift gate. See `useTenantBetFlags`.
   */
  betKey?: ExperimentalBetKey;
}

export interface PrimarySection {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Per-section accent color (hex or CSS var) for the sidebar active bar and secondary nav title. */
  accent?: string;
  /** First link is the default route when the primary icon is clicked. */
  links: NavLinkDef[];
}

/**
 * THE navigation tree. Edit here, nowhere else.
 */
export const PRIMARY_SECTIONS: PrimarySection[] = [
  {
    id: "home",
    label: "Home",
    icon: LayoutDashboard,
    accent: "#4F46E5",
    links: [
      { id: "overview", label: "Overview", to: "/", end: true },
      { id: "reports", label: "Reports", to: "/reports" },
      { id: "pilot-success", label: "Pilot success", to: "/pilot-success", indent: true, featureKey: "pilot_access" },
      { id: "audit", label: "Audit log", to: "/audit" },
      { id: "access-denials", label: "Access denials", to: "/access-denials" },
    ],
  },
  {
    id: "identities",
    label: "Identities",
    icon: Users,
    accent: "#2563EB",
    links: [
      { id: "all", label: "All identities", to: "/identities", end: true },
      { id: "workloads", label: "Machine identities", to: "/identities/workloads", indent: true },
      { id: "providers", label: "Identity providers", to: "/identity-providers" },
    ],
  },
  {
    // Cloud = cloud identity security surface (NHI, CIEM, posture, IAM drift).
    id: "cloud",
    label: "Cloud",
    icon: Cloud,
    accent: "#0EA5E9",
    links: [
      // NHI is the always-available landing for this section. It MUST stay first:
      // `PrimarySidebar` navigates the Cloud icon to `links[0].to`, and the former
      // first link (`/operations/aws`) is gated by `AwsMonitoringRouteGuard`, which
      // redirects to `/operations` whenever the `aws_monitoring` flag is off —
      // making the whole Cloud section (and every NHI link) unreachable.
      { id: "nhi-inventory", label: "NHI inventory", to: "/nhi-inventory", groupLabel: "Non-human identities" },
      { id: "nhi-posture", label: "NHI posture", to: "/nhi-posture", indent: true, groupLabel: "Non-human identities" },
      { id: "nhi-lifecycle", label: "NHI lifecycle", to: "/nhi-lifecycle", indent: true, groupLabel: "Non-human identities" },
      { id: "ciem", label: "Cloud entitlements", to: "/ciem", groupLabel: "Entitlements" },
      { id: "ciem-lifecycle", label: "CIEM lifecycle", to: "/ciem-lifecycle", indent: true, groupLabel: "Entitlements" },
      { id: "cloud-policy-drift", label: "Cloud IAM drift", to: "/cloud-policy-drift", groupLabel: "Entitlements" },
      // Gated to match its route guard: only show when AWS monitoring is enabled,
      // otherwise the link is a dead redirect to `/operations`.
      { id: "cloud-posture", label: "Cloud posture", to: "/operations/aws", featureKey: "aws_monitoring", groupLabel: "Posture" },
    ],
  },
  {
    id: "agents-nhi",
    label: "Agents & NHI",
    icon: Bot,
    accent: "#7C3AED",
    links: [
      { id: "assets-overview", label: "Overview", to: "/assets", end: true, groupLabel: "Overview" },
      { id: "ai-agents", label: "Agent catalog", to: "/ai-agents", groupLabel: "Agent fleet" },
      { id: "agent-inventory", label: "Governed fleet", to: "/agents", indent: true, groupLabel: "Agent fleet" },
      { id: "endpoint-agents", label: "Endpoint software agents", to: "/endpoint-agents", indent: true, groupLabel: "Agent fleet" },
      { id: "agent-intents", label: "Agentic broker", to: "/agent-intents", groupLabel: "Delegation & approvals" },
      { id: "agent-approvals", label: "Approvals", to: "/agent-approvals", indent: true, groupLabel: "Delegation & approvals" },
      { id: "agent-delegation", label: "Delegation", to: "/agent-delegation", indent: true, groupLabel: "Delegation & approvals" },
      { id: "mcp-gateway", label: "MCP Gateway", to: "/mcp-gateway", groupLabel: "Runtime" },
      { id: "agent-sessions", label: "Agent sessions", to: "/agent-sessions", indent: true, groupLabel: "Runtime" },
      { id: "secret-leases", label: "Secret leases", to: "/secret-leases", groupLabel: "Credentials" },
    ],
  },
  {
    id: "access",
    label: "Access",
    icon: UserCog,
    accent: "#D97706",
    links: [
      { id: "active", label: "Active sessions", to: "/sessions", end: true },
      { id: "oidc-clients", label: "OIDC clients", to: "/oidc-clients", indent: true },
      { id: "oidc-revocation-drill", label: "Revocation drill", to: "/oidc-revocation-drill", indent: true },
      { id: "protected-applications", label: "Protected applications", to: "/protected-applications", indent: true },
      { id: "mfa", label: "MFA challenges", to: "/mfa/challenges", indent: true },
      { id: "privileged-access", label: "Privileged access", to: "/privileged-access" },
      { id: "access-reviews", label: "Access reviews", to: "/access-reviews" },
      { id: "remote-access", label: "Remote access", to: "/remote-access" },
      { id: "browser-access", label: "Browser access", to: "/browser-access", betKey: "browser_native" },
    ],
  },
  {
    id: "policies",
    label: "Governance",
    icon: FileKey,
    accent: "#E11D48",
    links: [
      { id: "all", label: "All policies", to: "/policies", end: true },
      { id: "simulate", label: "Policy simulation", to: "/policies/simulate", indent: true },
      { id: "roles", label: "Roles", to: "/config/roles" },
      { id: "dsr", label: "Data subject requests", to: "/dsr" },
      { id: "agent-control-coverage", label: "Agent control coverage", to: "/agent-control-coverage" },
      { id: "policy-drift", label: "Policy drift", to: "/policy-drift" },
      { id: "shadow-mode", label: "Shadow mode", to: "/shadow-mode", indent: true },
    ],
  },
  {
    id: "trust",
    label: "Trust",
    icon: Lock,
    accent: "#0D9488",
    links: [
      { id: "trust-overview", label: "Trust Overview", to: "/trust-overview", end: true },
      { id: "revocation-receipts", label: "Revocation receipts", to: "/revocation-receipts" },
      { id: "predictive-pruning", label: "Predictive pruning", to: "/predictive-pruning" },
      { id: "trust-sla", label: "Trust SLAs", to: "/trust-sla", betKey: "trust_sla" },
      { id: "attack-path-whatif", label: "Attack-path what-if", to: "/attack-path-whatif" },
      { id: "trust-center", label: "Trust Center", to: "/trust-center" },
    ],
  },
  {
    id: "threats",
    label: "Threats",
    icon: AlertTriangle,
    accent: "#DC2626",
    links: [
      { id: "incidents", label: "Incidents", to: "/incidents", end: true },
      { id: "agentic-soc", label: "Agentic SOC", to: "/agentic-soc" },
      { id: "alerts", label: "Alerts", to: "/alerts" },
      { id: "playbooks", label: "Playbooks", to: "/playbooks" },
      { id: "threat-intel", label: "Threat intelligence", to: "/threat-intel", betKey: "federated_psi" },
      { id: "impossible-travel", label: "Impossible travel", to: "/impossible-travel" },
      { id: "decoy-secrets", label: "Planted decoys", to: "/decoy-secrets" },
    ],
  },
  {
    id: "verdikta",
    label: "Verdikta",
    icon: Radar,
    accent: "#4F46E5",
    links: [
      { id: "engine", label: "Confidence engine", to: "/verdikta", end: true },
      { id: "operations", label: "Operations", to: "/operations" },
      { id: "armour-graph", label: "Armour Graph", to: "/armour-graph" },
      { id: "copilot", label: "Armour Copilot", to: "/copilot" },
    ],
  },
  {
    // Configuration = pure config pages split from the former Platform section.
    id: "config",
    label: "Configuration",
    icon: Settings2,
    accent: "#0F766E",
    links: [
      { id: "idp-sync", label: "IdP sync", to: "/config/idp-sync", end: true },
      { id: "scim", label: "SCIM provisioning", to: "/config/scim" },
      { id: "diagnostics", label: "Diagnostics", to: "/config/diagnostics" },
      { id: "session-capture", label: "Session capture", to: "/config/session-capture" },
      { id: "oidc-provider", label: "OIDC Provider", to: "/config/oidc-provider" },
      { id: "connectors", label: "Connectors", to: "/settings?tab=connectors" },
    ],
  },
  {
    // Platform = settings + data integrations (config links moved to Configuration).
    id: "platform",
    label: "Platform",
    icon: Settings,
    accent: "#0F766E",
    links: [
      { id: "general", label: "Settings", to: "/settings", end: true, groupLabel: "Settings" },
      { id: "onboarding", label: "Onboarding", to: "/onboarding", end: true, groupLabel: "Settings" },
      { id: "integrations", label: "Integrations", to: "/integrations", groupLabel: "Data & integrations" },
      { id: "webhooks", label: "Webhooks", to: "/integrations/webhooks", indent: true, groupLabel: "Data & integrations" },
      { id: "shared-signals", label: "Shared Signals (CAEP)", to: "/shared-signals", indent: true, groupLabel: "Data & integrations" },
      { id: "ingestion", label: "Ingestion tools", to: "/config/ingestion", indent: true, groupLabel: "Data & integrations" },
      { id: "siem", label: "SIEM sinks", to: "/siem/sinks", groupLabel: "Data & integrations" },
      { id: "siem-dlq", label: "SIEM dead-letter queue", to: "/siem/dlq", indent: true, groupLabel: "Data & integrations" },
      { id: "vault", label: "Secrets vault", to: "/vault-secrets", groupLabel: "Data & integrations" },
      { id: "marketplace", label: "Marketplace", to: "/marketplace", groupLabel: "Data & integrations" },
    ],
  },
];

/**
 * Routes removed from nav but still reachable — preserve section ownership.
 */
const ORPHAN_ROUTE_OWNERS: Record<string, string> = {
  "/recordings": "access",
  "/dev/my-sessions": "access",
  "/agent-delegation-graph": "agents-nhi",
};

function normalizeSearch(search: string): string {
  if (!search) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

/**
 * Resolves which primary section "owns" a given URL.
 *
 * Uses **longest-prefix match** against every link's `to` path. Query-string
 * links (e.g. `/settings?tab=connectors`) match only when pathname + search
 * align exactly. This is deterministic and stable: clicking a sub-link never
 * changes which primary is highlighted, because each URL has exactly one owner
 * in the tree above.
 */
export function getActivePrimaryId(pathname: string, search = ""): string {
  if (pathname === "/") return "home";

  const normalizedSearch = normalizeSearch(search);
  const fullLocation = normalizedSearch ? `${pathname}${normalizedSearch}` : pathname;

  let bestScore = 0;
  let bestSectionId = "home";

  for (const section of PRIMARY_SECTIONS) {
    for (const link of section.links) {
      if (link.to === "/") continue;

      const qIdx = link.to.indexOf("?");
      const linkPath = qIdx >= 0 ? link.to.slice(0, qIdx) : link.to;
      const linkSearch = qIdx >= 0 ? link.to.slice(qIdx) : "";

      let score = 0;
      if (linkSearch) {
        if (fullLocation === `${linkPath}${linkSearch}`) {
          score = link.to.length + 1000;
        }
      } else {
        const exact = pathname === linkPath;
        const child = pathname.startsWith(linkPath + "/");
        if (exact || child) {
          score = linkPath.length;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestSectionId = section.id;
      }
    }
  }

  if (bestScore === 0 && ORPHAN_ROUTE_OWNERS[pathname]) {
    return ORPHAN_ROUTE_OWNERS[pathname];
  }

  return bestSectionId;
}

/** Looks up a primary section by id. */
export function getPrimarySection(id: string): PrimarySection | undefined {
  return PRIMARY_SECTIONS.find((s) => s.id === id);
}

/** All links across all sections — useful for tests / search. */
export function allNavLinks(): Array<NavLinkDef & { sectionId: string }> {
  const out: Array<NavLinkDef & { sectionId: string }> = [];
  for (const section of PRIMARY_SECTIONS) {
    for (const link of section.links) {
      out.push({ ...link, sectionId: section.id });
    }
  }
  return out;
}
